import { PieChart } from "react-minimal-pie-chart";
import { Link, useParams } from "react-router-dom";

import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  useFeatureFlagsConfig,
} from "~/featureFlags";
import { routes } from "~/router";

import { SiteDailyGoal } from "../../../../api/Api";
import { Icon } from "../../../../assets/icons/Icon";
import { formatNumber, getChartTextColor } from "../../../../helpers/helpers";
import Card from "../../../ui/card/Card";
import Spinner from "../../../ui/spinner/Spinner";
import styles from "./DailyGoals.module.scss";

const goals = {
  review_keywords: "Review the Keyword Rankings",
  build_brand_link: "Build one Foundation Link",
  build_niche_link: "Build one Authority Link",
  build_outreach_link: "Community Links",
  review_recommendations: "Review New Recommendations",
  review_interlinks: "Check Interlinks",
  set_focus_keyword: "Set Focus Keyword",
  review_ai_customizations: "Review AI Customizations",
};

type SiteDailyGoals = {
  siteDailyGoals: SiteDailyGoal[];
  isSiteDailyGoalsLoading: boolean;
};

type DailyGoalKind = SiteDailyGoal["kind"];
type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

const linkBuilders: Partial<Record<DailyGoalKind, (id: string) => string>> = {
  review_keywords: (id) => routes.site.keywords.list({ id }),
  build_niche_link: (id) => routes.site.links.niche({ id }),
  build_brand_link: (id) => routes.site.links.brand({ id }),
  review_ai_customizations: (id) => routes.site.content({ id }),
  review_recommendations: (id) => routes.site.onpage({ id }),
  build_outreach_link: (id) => routes.site.links.outreach({ id }),
  review_interlinks: (id) => routes.site.interlinks({ id }),
  set_focus_keyword: (id) => routes.site.keywords.list({ id }),
};

const goalFeatureFlag = {
  review_keywords: FEATURE_FLAGS.SITES_KEYWORDS,
  set_focus_keyword: FEATURE_FLAGS.SITES_KEYWORDS,

  build_niche_link: FEATURE_FLAGS.SITES_LINKS_NICHE,
  build_brand_link: FEATURE_FLAGS.SITES_LINKS_BRAND,
  build_outreach_link: FEATURE_FLAGS.SITES_LINKS_OUTREACH,

  review_recommendations: FEATURE_FLAGS.SITES_RECOMMENDATIONS,
  review_interlinks: FEATURE_FLAGS.SITES_INTERLINKS,
  review_ai_customizations: FEATURE_FLAGS.SITES_CUSTOMIZATIONS,
} satisfies Partial<Record<DailyGoalKind, FeatureFlag>>;

const DailyGoals = ({
  siteDailyGoals,
  isSiteDailyGoalsLoading,
}: SiteDailyGoals) => {
  const { id } = useParams<"id">();
  const flags = useFeatureFlagsConfig();

  if (!id) return null;

  const goalsWithLinks = siteDailyGoals
    .filter((g) => {
      const flag = goalFeatureFlag[g.kind];

      return flag ? isFeatureFlagEnabled(flags, flag) : false;
    })
    .map((g) => ({
      ...g,
      link: linkBuilders[g.kind]?.(id) ?? "",
    }));

  const completedCount = goalsWithLinks.filter((g) => g.completed).length;
  const totalCount = goalsWithLinks.length;
  const chartLabel = `${formatNumber(completedCount)} / ${formatNumber(totalCount)}`;

  return (
    <Card>
      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.dailyGoals}>
        <div className={styles.dailyGoalsHeader}>
          {isSiteDailyGoalsLoading && <Spinner />}
          <div className={styles.dailyGoalsHeaderChart}>
            {goalsWithLinks && goalsWithLinks?.length && (
              <PieChart
                lineWidth={15}
                label={() => chartLabel}
                startAngle={270}
                viewBoxSize={[100, 100]}
                labelStyle={{
                  fontSize: "1.5rem",
                  fill: getChartTextColor(),
                }}
                labelPosition={0}
                data={[...goalsWithLinks]
                  .sort(
                    (a, b) => (b?.completed ? 1 : 0) - (a?.completed ? 1 : 0),
                  )
                  .map((item) => {
                    return {
                      value: item?.completed ? 16.6 : 16.6,
                      color: item?.completed ? "#047BF8" : "#F2F4F8",
                    };
                  })}
              />
            )}
          </div>
          {/*
           // @ts-expect-error TS2551 non-existing className */}
          <div className={styles.dailyGoalsHeaderInfo}>
            <h3 className={styles.dailyGoalsHeaderTitle}>
              Your Daily Goal for Today
            </h3>
            <p className={styles.dailyGoalsHeaderDescription}>
              Hey! We've prepared daily goals for you. Achieve them every day
              and improve your website's productivity. And guess what? We're
              here to support you every step of the way ;)
            </p>
          </div>
        </div>
        <div className={styles.dailyGoalsItems}>
          {goalsWithLinks?.map((item, index) => {
            return (
              <Link
                key={index}
                className={styles.dailyGoalsItem}
                to={item.link}
              >
                {/*
                 // @ts-expect-error TS2551 non-existing className */}
                <div className={styles.dailyGoalsItemTitle}>
                  {" "}
                  {goals?.[item?.kind]}
                </div>
                {item?.completed ? (
                  <Icon iconName="GreenCheckmarkIcon" />
                ) : (
                  <Icon iconName="GreyCheckmarkIcon" />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
export default DailyGoals;
